/** Pure view-models for the dashboard widgets (no React), so they are unit-tested. */
import type { Query } from '../../api/client'
import type { Band, Dashboard, PlantMapTile } from '../../api/types'
import { areaLabel } from '../../lib/areas'
import { BAND_ORDER, bandMeta } from '../../lib/bands'

export const SNAPSHOT_DATE = '2024-12-30'

export interface DashboardFilters {
  area?: number
  dateFrom?: string
  dateTo?: string
  /** Equipment-list only: never included in aggregate API params. */
  band?: Band
  search?: string
}

export function filtersToParams(f: DashboardFilters): Query {
  return { area: f.area, date_from: f.dateFrom, date_to: f.dateTo }
}

/** "Corte del informe (dic 2024)": newest inspection per equipment up to the report date. */
export function isSnapshot(f: DashboardFilters): boolean {
  return f.dateTo === SNAPSHOT_DATE && !f.area && !f.dateFrom
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/
function validDate(text: string | null): string | undefined {
  if (!text || !ISO_DATE.test(text)) return undefined
  const d = new Date(`${text}T00:00:00Z`)
  return Number.isNaN(d.getTime()) || d.toISOString().slice(0, 10) !== text ? undefined : text
}

export function filtersToSearch(f: DashboardFilters): URLSearchParams {
  const p = new URLSearchParams()
  if (f.area) p.set('area', String(f.area))
  if (f.dateFrom) p.set('from', f.dateFrom)
  if (f.dateTo) p.set('to', f.dateTo)
  if (f.band && BAND_ORDER.includes(f.band)) p.set('band', f.band)
  if (f.search?.trim()) p.set('search', f.search.trim())
  return p
}

export function filtersFromSearch(p: URLSearchParams): DashboardFilters {
  const out: DashboardFilters = {}
  const area = p.get('area')
  if (area && /^\d+$/.test(area)) out.area = Number(area)
  const from = validDate(p.get('from'))
  const to = validDate(p.get('to'))
  if (from) out.dateFrom = from
  if (to) out.dateTo = to
  const band = p.get('band') as Band | null
  if (band && BAND_ORDER.includes(band)) out.band = band
  const search = p.get('search')?.trim()
  if (search) out.search = search
  return out
}

/* ---------- KPIs ---------- */

type Kpis = Dashboard['kpis']
export interface KpiModel {
  key: 'total' | 'critical' | 'preventive' | 'not_assessed' | 'work_orders'
  label: string
  value: string
  caption: string
  tone: 'brand' | 'blue' | 'green' | 'pink' | 'peri'
  band?: Band
}

function share(part: number, total: number): string {
  return `${total ? Math.round((100 * part) / total) : 0}% del total`
}

export function kpiCards(k: Kpis): KpiModel[] {
  return [
    { key: 'total', label: 'Total de equipos', value: String(k.total_equipment), caption: `${k.route_critical_at_risk} de ruta crítica en riesgo`, tone: 'brand' },
    { key: 'critical', label: 'Críticos', value: String(k.critical), caption: share(k.critical, k.total_equipment), tone: 'pink', band: 'critical' },
    { key: 'preventive', label: 'Preventivos', value: String(k.preventive), caption: share(k.preventive, k.total_equipment), tone: 'blue', band: 'preventive' },
    { key: 'not_assessed', label: 'Sin evaluar', value: String(k.not_assessed), caption: share(k.not_assessed, k.total_equipment), tone: 'green', band: 'not_assessed' },
    { key: 'work_orders', label: 'Órdenes de trabajo abiertas', value: String(k.open_work_orders), caption: `${k.overdue_work_orders} ${k.overdue_work_orders === 1 ? 'vencida' : 'vencidas'}`, tone: 'peri' },
  ]
}

/* ---------- charts ---------- */

export function bandDonut(distribution: Dashboard['band_distribution']) {
  const legend = distribution.map((d) => ({ ...d, label: bandMeta(d.band).label, color: bandMeta(d.band).color }))
  return {
    legend,
    slices: legend.filter((d) => d.count > 0),
    total: distribution.reduce((sum, d) => sum + d.count, 0),
  }
}

export function stackedByArea(byArea: Dashboard['by_area']) {
  return byArea.map((a) => ({ area: a.area, code: a.code, name: a.name, total: a.total, ...a.counts }))
}

export interface ScoreBar {
  area: number
  code: string
  name: string
  value: number | null
  widthPct: number
  label: string
  worstBand: string
}

export function avgScoreBars(byArea: Dashboard['by_area']): ScoreBar[] {
  const values = byArea.map((a) => (a.avg_score === null ? null : Number(a.avg_score)))
  const max = Math.max(0, ...values.filter((v): v is number => v !== null))
  return byArea
    .map((a, i) => ({ a, v: values[i] }))
    .sort((x, y) => (y.v ?? -1) - (x.v ?? -1) || x.a.sort_order - y.a.sort_order)
    .map(({ a, v }) => ({
      area: a.area, code: a.code, name: a.name, value: v, worstBand: a.worst_band,
      widthPct: v === null || max === 0 ? 0 : Math.round((100 * v) / max),
      label: v === null ? '—' : v.toFixed(1),
    }))
}

const MONTHS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic']

export function trendSeries(trend: Dashboard['score_trend']) {
  const points = trend.points.map((p) => {
    const [year, month] = p.month.split('-')
    return {
      month: p.month,
      label: `${MONTHS[Number(month) - 1] ?? month} ${year}`,
      avg: p.avg_score === null ? null : Number(p.avg_score),
      inspections: p.inspections,
      equipment: p.equipment,
    }
  })
  return { points, sparse: trend.points.length < 6, note: trend.note }
}

export function closeTimeRows(rows: Dashboard['wo_close_time_by_area']) {
  const values = rows.map((r) => (r.avg_days_to_close === null ? null : Number(r.avg_days_to_close)))
  const max = Math.max(0, ...values.filter((v): v is number => v !== null))
  return rows.map((r, i) => {
    const v = values[i]
    return {
      area: r.area, code: r.code, name: r.name, closed: r.closed,
      label: v === null ? 'Sin órdenes cerradas' : `${v} días`,
      widthPct: v === null || max === 0 ? 0 : Math.round((100 * v) / max),
      pills: { on_time: r.on_time, late: r.late, too_late: r.too_late, no_due_date: r.no_due_date },
    }
  })
}

/* ---------- plant map ---------- */

const RANK: Record<string, number> = { critical: 4, preventive: 3, warning: 2, stable: 1, not_assessed: 0 }

export function rankAreas(tiles: PlantMapTile[], search = ''): PlantMapTile[] {
  const needle = search.trim().toLowerCase()
  return tiles
    .filter((t) => !needle || `${t.name} ${t.code} ${areaLabel(t.code, t.name)}`.toLowerCase().includes(needle))
    .sort((a, b) => {
      const byBand = (RANK[b.worst_band] ?? 0) - (RANK[a.worst_band] ?? 0)
      if (byBand) return byBand
      const av = a.avg_score === null ? -1 : Number(a.avg_score)
      const bv = b.avg_score === null ? -1 : Number(b.avg_score)
      return bv - av || a.step - b.step
    })
}

export interface PlantLayout {
  main: PlantMapTile[]
  branches: { tile: PlantMapTile; parentIndex: number }[]
  utilities: PlantMapTile[]
}

export function plantMapLayout(tiles: PlantMapTile[]): PlantLayout {
  const ordered = [...tiles].sort((a, b) => a.step - b.step)
  const main = ordered.filter((t) => t.lane === 'main')
  const branches = ordered
    .filter((t) => t.lane === 'branch')
    .map((tile) => ({ tile, parentIndex: Math.max(0, main.findIndex((m) => m.code === tile.after)) }))
  return { main, branches, utilities: ordered.filter((t) => t.lane === 'utility') }
}

export { BAND_ORDER }

/* ---------- trend grouping (month / quarter / year toggle) ---------- */

export type TrendGrain = 'month' | 'quarter' | 'year'
export type TrendPoint = ReturnType<typeof trendSeries>['points'][number]

export function groupTrend(points: TrendPoint[], grain: TrendGrain): TrendPoint[] {
  if (grain === 'month') return points
  const groups = new Map<string, { label: string; sum: number; weight: number; inspections: number; equipment: number; first: string }>()
  for (const p of points) {
    const [year, month] = p.month.split('-')
    const key = grain === 'year' ? year : `${year}-Q${Math.floor((Number(month) - 1) / 3) + 1}`
    const label = grain === 'year' ? year : `T${Math.floor((Number(month) - 1) / 3) + 1} ${year}`
    const g = groups.get(key) ?? { label, sum: 0, weight: 0, inspections: 0, equipment: 0, first: p.month }
    if (p.avg !== null) {
      g.sum += p.avg * p.inspections
      g.weight += p.inspections
    }
    g.inspections += p.inspections
    g.equipment = Math.max(g.equipment, p.equipment)
    groups.set(key, g)
  }
  return [...groups.values()].map((g) => ({
    month: g.first, label: g.label, avg: g.weight ? Math.round((g.sum / g.weight) * 100) / 100 : null,
    inspections: g.inspections, equipment: g.equipment,
  }))
}
